/**
 * System module translations - English
 * Includes: user, role, menu, dept, organization, permission, logs, businessType
 */
export default {
  systemMgmt: {
    // ======== User Management ========
    user: {
      title: 'User Management',
      subtitle: 'Manage system user accounts',
      allUsers: 'All',
      staffUsers: 'Staff',
      merchantUsers: 'Merchant',
      userType: 'Type',
      staffLabel: 'Staff',
      merchantLabel: 'Merchant',
      addUser: 'Add User',
      editUser: 'Edit User',
      userList: 'User List',
      username: 'Username',
      userCode: 'User Code',
      realname: 'Real Name',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      newPassword: 'New Password',
      gender: 'Gender',
      male: 'Male',
      female: 'Female',
      dept: 'Department',
      deptName: 'Department',
      role: 'Role',
      roleAssign: 'Role',
      assignRole: 'Assign Role',
      selectRole: 'Select Role',
      org: 'Organization',
      headquarters: 'Headquarters',
      store: 'Store',
      notSet: 'Not Set',
      unassignedOrg: 'Unassigned',
      batchExport: 'Batch Export',
      batchImport: 'Batch Import',
      resetPassword: 'Reset Password',
      wxAccount: 'WeChat',
      wxBoundYes: 'Bound',
      wxBoundNo: 'Not bound',
      unbindWx: 'Unbind WeChat',
      unbindWxConfirm: 'Unbind WeChat Mini Program for user "{name}"? They must re-bind to log in with WeChat.',
      unbindWxSuccess: 'WeChat unbound successfully',
      unbindWxFailed: 'Failed to unbind WeChat',
      searchPlaceholder: 'Search username, name or email',
      usernamePlaceholder: 'Username',
      realnamePlaceholder: 'Real Name',
      passwordPlaceholder: 'Enter password',
      confirmPasswordPlaceholder: 'Confirm password',
      newPasswordPlaceholder: 'Enter new password',
      emailPlaceholder: 'Enter email',
      phonePlaceholder: 'Enter phone number',
      remarkPlaceholder: 'Remark',
      orgPlaceholder: 'Select organization',
      deptPlaceholder: 'Select department',
      realnamePlaceholderInput: 'Enter real name',
      usernamePlaceholderInput: 'Enter username',
      orgFilterTip: 'Select organization first, departments will auto-filter',
      deptFilterTip: 'After selecting organization, only its departments are shown',
      // Validation
      usernameRequired: 'Username is required',
      usernameLength: 'Username must be 3-20 characters',
      usernamePattern: 'Username can only contain letters, numbers and underscores',
      passwordRequired: 'Password is required',
      passwordLength: 'Password must be 8-32 characters',
      confirmPasswordRequired: 'Please confirm your password',
      passwordMismatch: 'Passwords do not match',
      realnameRequired: 'Real name is required',
      realnameLength: 'Name must be 2-10 characters',
      orgRequired: 'Please select an organization',
      deptRequired: 'Please select a department',
      roleRequired: 'Please select at least one role',
      statusRequired: 'Please select a status',
      newPasswordRequired: 'New password is required',
      // Messages
      fetchFailed: 'Failed to fetch user list',
      editSuccess: 'Updated successfully',
      addSuccess: 'Created successfully',
      deleteConfirm: 'Are you sure to delete user "{name}"?',
      deleteConfirmIrreversible: 'Are you sure to delete user "{name}"? This cannot be undone!',
      batchDeleteConfirm: 'Are you sure to delete {count} selected users?',
      batchDeleteConfirmIrreversible: 'Are you sure to delete {count} selected users? This cannot be undone!',
      batchDeleteTitle: 'Batch Delete Confirmation',
      batchDeleteSuccess: 'Batch delete successful',
      batchDeleteFailed: 'Batch delete failed',
      toggleStatusConfirm: 'Are you sure to {action} user "{name}"?',
      toggleStatusTitle: '{action} Confirmation',
      resetPasswordConfirm: 'Are you sure to reset password for user "{name}"? The password will be set to system default.',
      resetPasswordTitle: 'Reset Password Confirmation',
      resetPasswordSuccess: 'Password reset successful',
      resetPasswordFailed: 'Failed to reset password',
      resetPasswordSuccessWithPwd: 'Password reset successful, new password: {pwd}',
      userInvalid: 'Invalid user information',
      roleAssignSuccess: 'Role assignment successful',
      roleAssignFailed: 'Role assignment failed',
      fetchRolesFailed: 'Failed to fetch role list',
      exportLoading: 'Exporting user data...',
      exportSuccess: 'Export successful',
      exportFailed: 'Export failed',
      orgLoadFailed: 'Failed to load organization list',
      orgLoadError: 'Failed to load organizations, please refresh the page',
      // Import
      importTitle: 'Batch Import Users',
      importDesc: 'Import Instructions',
      importFileTip: 'Only .xlsx/.xls format supported',
      importSizeTip: 'File size must not exceed 10MB',
      downloadTemplate: 'Download Template',
      dataPreview: 'Data Preview',
      importResult: 'Import Result',
      totalCount: 'Total',
      successCount: 'Success',
      failCount: 'Failed',
      startImport: 'Start Import',
      // Export
      exportTitle: 'Batch Export Users',
      exportDesc: 'Export Instructions',
      exportOptions: 'Export Options',
      exportRange: 'Export Range',
      currentQueryResult: 'Current Query Result',
      allUserData: 'All User Data',
      exportFields: 'Export Fields',
      selectAll: 'Select All',
      clearAll: 'Clear All',
      defaultFields: 'Default Fields',
      fileName: 'File Name',
      includePassword: 'Include Password',
      include: 'Include',
      exclude: 'Exclude',
      securityWarning: 'Exporting passwords is a security risk, proceed with caution',
      exportProgress: 'Export Progress',
      downloadFile: 'Download File',
      fieldUsername: 'Username',
      fieldRealname: 'Real Name',
      fieldEmail: 'Email',
      fieldPhone: 'Phone',
      fieldDept: 'Department',
      fieldRole: 'Role',
      fieldStatus: 'Status',
      fieldCreateTime: 'Created',
      fieldLastLogin: 'Last Login',
      fieldRemark: 'Remark',
    },

    // ======== Role Management ========
    role: {
      title: 'Role Management',
      subtitle: 'Manage system roles and permission configuration',
      addRole: 'Add Role',
      editRole: 'Edit Role',
      roleList: 'Role List',
      roleName: 'Role Name',
      roleKey: 'Role Key',
      roleKeyAlt: 'Permission Key',
      roleSort: 'Sort',
      displayOrder: 'Display Order',
      roleDesc: 'Description',
      permConfig: 'Permission Config',
      permAssign: 'Permissions',
      dataScope: 'Data Scope',
      searchPlaceholder: 'Search role name or description',
      roleNamePlaceholder: 'Enter role name',
      roleKeyPlaceholder: 'Enter role key',
      roleKeyInputPlaceholder: 'Enter permission key',
      statusPlaceholder: 'Select status',
      remarkPlaceholder: 'Enter role remark',
      roleKeyTip: 'The permission key will be used to identify this role in the system',
      // Validation
      roleNameRequired: 'Role name is required',
      roleNameLength: 'Role name must be 2-50 characters',
      roleNameExists: 'Role name already exists',
      roleKeyRequired: 'Permission key is required',
      roleKeyLength: 'Permission key must be 2-100 characters',
      roleKeyExists: 'Permission key already exists',
      roleKeyPattern: 'Role key can only contain letters, numbers and underscores',
      roleKeyCheckFailed: 'Failed to verify role key',
      roleSortRequired: 'Display order is required',
      roleSortRange: 'Display order must be between 0 and 999',
      // Messages
      fetchFailed: 'Failed to fetch role list',
      roleUpdateSuccess: 'Role updated successfully',
      roleCreateSuccess: 'Role created successfully',
      roleUpdateFailed: 'Failed to update role',
      roleCreateFailed: 'Failed to create role',
      saveFailed: 'Save failed',
      deleteConfirm: 'Are you sure to delete role "{name}"?',
      deleteConfirmIrreversible: 'Are you sure to delete role "{name}"? This cannot be undone!',
      batchDeleteConfirm: 'Are you sure to delete {count} selected roles? This cannot be undone!',
      batchDeleteTitle: 'Batch Delete Confirmation',
      batchDeleteSuccess: 'Batch delete successful',
      batchDeleteFailed: 'Batch delete failed',
      toggleStatusConfirm: 'Are you sure to {action} role "{name}"?',
      toggleStatusTitle: '{action} Confirmation',
      fetchInfoFailed: 'Failed to fetch role info',
      exportLoading: 'Exporting role data...',
      // Permission Config
      menuAndPermission: 'Menu & Function Permissions',
      menuAndPermissionDesc: 'Configure the menus, pages and operations accessible to this role',
      permissionConfig: 'Permission Config',
      expandAll: 'Expand All',
      collapseAll: 'Collapse All',
      checkAll: 'Check All',
      uncheckAll: 'Uncheck All',
      selectedPermissions: 'Selected Permissions',
      noPermissionSelected: 'No permissions selected',
      savePermConfig: 'Save Permission Config',
      roleInvalid: 'Invalid role information',
      notSelected: 'No role selected',
      permSaveSuccess: 'Permission config saved ({count} operations)',
      permSavePartial: 'Partially saved ({success} succeeded, {failed} failed): {errors}',
      permSaveFailed: 'Permission save failed: {errors}',
      openPermFailed: 'Failed to open permission config',
      fetchPermTreeFailed: 'Failed to fetch permission tree',
      // Data Scope
      dataPermission: 'Data Permission',
      dataAccessScope: 'Data Access Scope',
      dataAccessScopeDesc: 'Configure the data range accessible to this role',
      dataScopeRange: 'Data Permission Scope',
      allDataScope: 'All Data',
      allDataScopeDesc: 'Can view all data',
      customDataScope: 'Custom Data Scope',
      customDataScopeDesc: 'Can view specified department data',
      deptDataScope: 'Own Department Data',
      deptDataScopeDesc: 'Can only view own department data',
      deptAndChildDataScope: 'Department & Sub-departments',
      deptAndChildDataScopeDesc: 'Can view own department and sub-department data',
      selfDataScope: 'Own Data Only',
      selfDataScopeDesc: 'Can only view and operate own data',
      customDeptPerm: 'Custom Department Permission',
      customDeptRequired: 'Custom data scope requires selecting departments',
      dataScopeSaveSuccess: 'Data scope config saved successfully',
      dataScopeSaveFailed: 'Failed to save data scope config',
      fetchDeptTreeFailed: 'Failed to fetch department tree',
      fetchDataScopeFailed: 'Failed to fetch role data scope',
      selectDeptTip: 'Select departments accessible to this role:',
      selectDeptPlaceholder: 'Select department',
      deptTip: 'Selected departments determine the data range accessible to this role',
      saveConfig: 'Save Config',
      // Menu Types
      menuTypeDir: 'Directory',
      menuTypeMenu: 'Menu',
      menuTypeBtn: 'Function',
    },

    // ======== Menu Management ========
    menu: {
      title: 'Menu Management',
      subtitle: 'Manage system menu structure and permission config',
      addMenu: 'Add Menu',
      editMenu: 'Edit Menu',
      menuList: 'Menu List',
      menuName: 'Menu Name',
      menuType: 'Menu Type',
      menuIcon: 'Menu Icon',
      orderNum: 'Sort',
      displayOrder: 'Display Order',
      routePath: 'Route Path',
      component: 'Component Path',
      perms: 'Permission Key',
      visibleStatus: 'Visible Status',
      menuStatus: 'Menu Status',
      parentMenu: 'Parent Menu',
      addChildMenu: 'Add Sub-menu',
      mainDir: 'Root Directory',
      typeDir: 'Directory',
      typeMenu: 'Menu',
      typeBtn: 'Button',
      show: 'Show',
      hide: 'Hide',
      collapseAll: 'Collapse All',
      expandAll: 'Expand All',
      menuNamePlaceholder: 'Enter menu name',
      parentMenuPlaceholder: 'Select parent menu',
      routePathPlaceholder: 'Enter route path',
      componentPlaceholder: 'Enter component path',
      iconPlaceholder: 'Enter icon name',
      permsPlaceholder: 'Enter permission key, e.g.: system:user:list',
      remarkPlaceholder: 'Enter remark',
      // Validation
      menuNameRequired: 'Menu name is required',
      menuNameLength: 'Must be 2-50 characters',
      menuTypeRequired: 'Please select menu type',
      orderNumRequired: 'Display order is required',
      routePathRequired: 'Route path is required',
      // Messages
      fetchFailed: 'Failed to fetch menu list',
      fetchInfoFailed: 'Failed to fetch menu info',
      deleteConfirm: 'Are you sure to delete menu "{name}"? This cannot be undone!',
      deleteWarning: 'Warning',
      deleteFailed: 'Failed to delete menu',
      statusUpdateSuccess: 'Status updated',
      statusUpdateFailed: 'Failed to update status',
      createSuccess: 'Created successfully',
      updateSuccess: 'Updated successfully',
    },

    // ======== Department Management ========
    dept: {
      title: 'Department Management',
      subtitle: 'Manage organizational structure and department hierarchy',
      addDept: 'Add Department',
      editDept: 'Edit Department',
      deptList: 'Department List',
      deptName: 'Department Name',
      deptCode: 'Department Code',
      parentDept: 'Parent Department',
      belongOrg: 'Organization',
      leader: 'Leader',
      leaderPhone: 'Contact Phone',
      sortOrder: 'Display Order',
      deptUsers: 'Department Users',
      assignLeader: 'Assign Leader',
      orgFilter: 'Filter by Organization',
      filterByOrg: 'Filter by Organization',
      expandAll: 'Expand All',
      collapseAll: 'Collapse All',
      root: 'Root',
      notBound: 'Not Bound',
      notSet: 'Not Set',
      headquarters: 'Headquarters',
      store: 'Store',
      searchPlaceholder: 'Department name, code or leader',
      deptNamePlaceholder: 'Enter department name',
      deptCodePlaceholder: 'Enter department code',
      parentDeptPlaceholder: 'Select parent department (leave empty for root)',
      orgPlaceholder: 'Select organization',
      orgFilterPlaceholder: 'Select organization',
      leaderPlaceholder: 'Enter leader name',
      phonePlaceholder: 'Enter phone number',
      emailPlaceholder: 'Enter email address',
      sortPlaceholder: 'Sort order',
      remarkPlaceholder: 'Enter department remark',
      orgTip: 'Each department must belong to an organization (HQ or store)',
      codeTip: 'Department code is used for internal identification',
      activeText: 'On',
      inactiveText: 'Off',
      // Department Category
      deptCategory: 'Dept Type',
      deptCategoryPlaceholder: 'Select department type',
      categoryHeadquarters: 'Headquarters',
      categoryProject: 'Project',
      categoryStore: 'Project/Store',
      categoryNormal: 'General',
      categorySales: 'Sales',
      categoryPurchase: 'Purchasing',
      categoryWarehouse: 'Warehouse',
      categoryFinance: 'Finance',
      categoryOperation: 'Operations',
      categoryInvestment: 'Investment',
      isLeaf: 'Leaf Dept',
      isLeafTip: 'Once marked as leaf, no child departments can be created under this department',
      segmentPlaceholder: 'Level {level} code ({length} digits)',
      codeNumeric: 'Code segment must be numeric',
      // Validation
      orgRequired: 'Please select an organization',
      parentDeptRequired: 'Please select a parent department',
      deptCodeRequired: 'Department code is required',
      deptNameRequired: 'Department name is required',
      deptNameLength: 'Department name must be 2-50 characters',
      sortOrderRequired: 'Display order is required',
      sortOrderRange: 'Sort order must be between 0 and 999',
      phonePattern: 'Please enter a valid phone number',
      emailPattern: 'Please enter a valid email address',
      statusRequired: 'Please select a status',
      // Messages
      fetchFailed: 'Failed to fetch department list',
      deleteConfirm: 'Are you sure to delete department "{name}"? This cannot be undone!',
      createSuccess: 'Created successfully',
      updateSuccess: 'Updated successfully',
      orgLoadFailed: 'Failed to load organization list',
      orgLoadError: 'Failed to load organizations, please refresh the page',
      // Department Users
      deptUsersTitle: 'Department Users - {name}',
      searchUserPlaceholder: 'Search username, name or phone',
      joinTime: 'Join Date',
      userDetail: 'User Detail',
      lastLogin: 'Last Login',
      neverLogin: 'Never logged in',
      fetchUsersFailed: 'Failed to fetch department users',
      // Leader Assignment
      assignLeaderTitle: 'Assign Department Leader',
      selectUser: 'Select User',
      selectLeaderPlaceholder: 'Select leader',
      notAssigned: 'Not Assigned',
      leaderPermTip: 'The leader will have management permission for this department',
      positionDesc: 'Position Description',
      positionPlaceholder: 'Enter leader position',
      effectiveTime: 'Effective Time',
      effectiveTimePlaceholder: 'Select effective time',
      effectiveTimeTip: 'Leave empty to take effect immediately',
      leaderHistory: 'Leader History',
      noLeaderHistory: 'No leader records',
      confirmAssign: 'Confirm Assignment',
      assignSuccess: 'Leader assigned successfully',
      assignFailed: 'Failed to assign leader',
      selectNodeTip: 'Select a department from the tree to view details',
      childDeptList: 'Sub-departments',
    },

    // ======== Organization Management ========
    org: {
      title: 'Organization Management',
      subtitle: 'Manage organizational structure (HQ, stores/projects)',
      addOrg: 'Add Organization',
      editOrg: 'Edit Organization',
      orgList: 'Organization List',
      orgName: 'Organization Name',
      orgCode: 'Organization Code',
      orgLevel: 'Organization Level',
      parentOrg: 'Parent Organization',
      leader: 'Leader',
      leaderPhone: 'Leader Phone',
      contactPhone: 'Contact Phone',
      address: 'Address',
      sortOrder: 'Sort',
      displayOrder: 'Display Order',
      businessScope: 'Business Scope',
      totalArea: 'Total Building Area',
      rentableArea: 'Rentable Area',
      headquarters: 'Headquarters',
      storeProject: 'Store/Project',
      deptLevel: 'Department',
      teamLevel: 'Team',
      searchPlaceholder: 'Organization name or code',
      orgNamePlaceholder: 'Enter organization name',
      orgCodePlaceholder: 'Enter organization code',
      parentOrgPlaceholder: 'Select parent organization (leave empty for root)',
      orgLevelPlaceholder: 'Select organization level',
      leaderPlaceholder: 'Enter leader name',
      leaderPhonePlaceholder: 'Enter leader phone',
      contactPhonePlaceholder: 'Enter contact phone',
      addressPlaceholder: 'Enter address',
      totalAreaPlaceholder: 'Enter total building area',
      rentableAreaPlaceholder: 'Enter rentable area',
      businessScopePlaceholder: 'Enter business scope',
      remarkPlaceholder: 'Enter remark',
      sortPlaceholder: 'Sort order',
      codeTip: 'Organization code is used for internal identification',
      levelTip: 'Only HQ and Store/Project levels are supported',
      areaTip: 'Unit: m\u00B2',
      rentableAreaTip: 'Unit: m\u00B2, base area for business planning',
      // Validation
      orgNameRequired: 'Organization name is required',
      orgNameLength: 'Organization name must be 2-100 characters',
      orgCodeRequired: 'Organization code is required',
      orgCodeLength: 'Organization code must be 2-50 characters',
      orgCodePattern: 'Code can only contain letters, numbers, underscores and hyphens',
      orgLevelRequired: 'Please select organization level',
      sortOrderRange: 'Sort order must be between 0 and 999',
      leaderPhonePattern: 'Please enter a valid phone number',
      contactPhonePattern: 'Invalid contact phone format',
      statusRequired: 'Please select a status',
      // Messages
      fetchFailed: 'Failed to fetch organization list',
      deleteConfirm: 'Are you sure to delete organization "{name}"? This cannot be undone!',
      createSuccess: 'Created successfully',
      updateSuccess: 'Updated successfully',
    },

    // ======== Permission Assignment ========
    permAssign: {
      title: 'Permission Assignment',
      subtitle: 'Manage department resource jurisdiction and user resource permissions',
      deptResource: 'Department Resource Jurisdiction',
      userResource: 'User Resource Permission',
      assignDeptPerm: 'Assign Jurisdiction',
      assignUserPerm: 'Assign User Permission',
      resourceType: 'Resource Type',
      resourceId: 'Resource ID',
      manageDept: 'Managing Dept',
      manageType: 'Management Type',
      permissionScope: 'Permission Scope',
      permissionType: 'Permission Type',
      effectiveTime: 'Effective Time',
      expireTime: 'Expire Time',
      startTimePlaceholder: 'Start time',
      endTimePlaceholder: 'End time',
      selectDeptPlaceholder: 'Select department',
      selectUserPlaceholder: 'Select user',
      selectResourceType: 'Select resource type',
      selectManageType: 'Select management type',
      selectPermType: 'Select permission type',
      inputResourceId: 'Enter resource ID',
      scopeDescPlaceholder: 'Describe permission scope',
      remarkPlaceholder: 'Remark',
      assignDeptTitle: 'Assign Department Jurisdiction',
      assignUserTitle: 'Assign User Resource Permission',
      query: 'Query',
      // Validation
      resourceTypeRequired: 'Resource type is required',
      resourceIdRequired: 'Resource ID is required',
      deptRequired: 'Please select a department',
      manageTypeRequired: 'Management type is required',
      userRequired: 'Please select a user',
      permTypeRequired: 'Permission type is required',
      // Messages
      fetchResourceTypesFailed: 'Failed to load resource types',
      fetchManageTypesFailed: 'Failed to load management types',
      fetchPermTypesFailed: 'Failed to load permission types',
      fetchDeptsFailed: 'Failed to load department list',
      fetchUsersFailed: 'Failed to load user list',
      fetchDeptPermFailed: 'Failed to load department jurisdiction',
      fetchUserPermFailed: 'Failed to load user permissions',
      statusUpdateSuccess: 'Status updated',
      statusUpdateFailed: 'Failed to update status',
      deleteConfirm: 'Are you sure to delete this jurisdiction record?',
      userPermToggleTodo: 'User permission status toggle to be implemented',
      userPermDeleteTodo: 'User permission delete to be implemented',
      deptPrefix: 'Dept',
      userPrefix: 'User',
    },

    // ======== Permission Matrix ========
    permMatrix: {
      title: 'Permission Matrix',
      subtitle: 'Configure data access permission matrix for users or roles',
      resourceType: 'Resource Type',
      resourceTypePlaceholder: 'Select resource type',
      permSubject: 'Permission Subject',
      permSubjectPlaceholder: 'Select permission subject',
      userPerm: 'User Permission',
      rolePerm: 'Role Permission',
      selectUserRole: 'Select User/Role',
      loadMatrix: 'Load Permission Matrix',
      resetPerm: 'Reset Permissions',
      savePermConfig: 'Save Permission Config',
      permPreview: 'Permission Preview',
      currentConfig: 'Current Config',
      permRange: 'Permission Range',
      lastUpdate: 'Last Updated',
      customPermConfig: 'Custom Permission Config',
      operationType: 'Operation Type',
      permExpression: 'Permission Expression',
      configDesc: 'Config Description',
      sqlPlaceholder: 'SQL filter condition',
      configHelp: 'Permission expression is used for data filtering; leave empty for default policy',
      scopeAll: 'All Data',
      scopeAllDesc: 'Can access all data',
      scopeCustom: 'Custom Data',
      scopeCustomDesc: 'Filtered by specified conditions',
      scopeDept: 'Own Department Data',
      scopeDeptDesc: 'Own department data only',
      scopeDeptAndChild: 'Dept & Sub-departments',
      scopeDeptAndChildDesc: 'Own department and sub-department data',
      scopeSelf: 'Personal Data',
      scopeSelfDesc: 'Only data created by self',
      authorized: 'Authorized',
      unauthorized: 'Unauthorized',
      deptData: 'Department Data',
      assetData: 'Asset Data',
      merchantData: 'Merchant Data',
      opView: 'View',
      opAdd: 'Add',
      opEdit: 'Edit',
      opDelete: 'Delete',
      noData: 'None',
      configItemsCount: '{configured} / {total} items configured',
      customConfigBtn: 'Custom Config',
      configHelpTitle: 'Config Help',
      configHelpLine1: 'Supports SQL condition expressions to define permission scope',
      configHelpLine2: 'You can use field names, comparison operators, IN, LIKE, etc.',
      configHelpLine3: 'Example: dept_id IN (1,2,3) or create_time >= \'2024-01-01\'',
      sqlExpressionRequired: 'Please enter permission expression',
      customConfigSaved: 'Custom config saved successfully',
      permResetSuccess: 'Permission config has been reset',
      permSaveSuccess: 'Permission config saved successfully',
      permLoadSuccess: 'Permission matrix loaded successfully',
      resourceTypeEmpty: 'Resource type data is empty',
      matrixTitle: '{resourceType} - {subjectType} Permission Matrix',
      userLabel: 'User',
      roleLabel: 'Role',
      selectUserRolePlaceholder: 'Select user or role',
      // Messages
      resetConfirm: 'Are you sure to reset the current permission config? This cannot be undone.',
      resetTitle: 'Reset Permissions',
      saveConfirm: 'Are you sure to save the current permission matrix?',
      loadFailed: 'Failed to load permission matrix',
      saveFailed: 'Save failed',
      loadResourceTypesFailed: 'Failed to load resource types',
      loadSubjectOptionsFailed: 'Failed to load user/role list',
    },

    // ======== Operation Logs ========
    logs: {
      title: 'Operation Logs',
      subtitle: 'View system operation records and audit logs',
      logList: 'Log List',
      logDetail: 'Log Detail',
      operStatus: 'Operation Status',
      operType: 'Operation Type',
      dateRange: 'Date Range',
      to: 'to',
      searchPlaceholder: 'Operator, IP or description',
      id: 'ID',
      operator: 'Operator',
      operDesc: 'Description',
      ipAddress: 'IP Address',
      responseTime: 'Duration (ms)',
      operTime: 'Operation Time',
      module: 'Module',
      function: 'Function',
      requestMethod: 'Request Method',
      requestParams: 'Request Params',
      responseResult: 'Response Result',
      errorMsg: 'Error Message',
      success: 'Success',
      fail: 'Failed',
      // Operation Types
      typeLogin: 'Login',
      typeLogout: 'Logout',
      typeAdd: 'Add',
      typeUpdate: 'Update',
      typeDelete: 'Delete',
      typeQuery: 'Query',
      typeExport: 'Export',
      typeImport: 'Import',
      typeGrant: 'Grant',
      typeTest: 'Test',
      // Messages
      fetchFailed: 'Failed to fetch log list',
    },

    // ======== Fee Item ========
    feeItem: {
      title: 'Fee Item Management',
      subtitle: 'Manage contract fee items with various billing methods',
      addItem: 'Add Fee Item',
      editItem: 'Edit Fee Item',
      itemList: 'Fee Item List',
      itemCode: 'Fee Code',
      itemName: 'Fee Name',
      category: 'Category',
      calcType: 'Billing Method',
      defaultValue: 'Default Value',
      unit: 'Unit',
      sortOrder: 'Sort',
      isSystem: 'System',
      isRequired: 'Required',
      remark: 'Remark',
      invoiceSettings: 'Invoice Settings',
      invoiceGoodsName: 'Invoice Item Name',
      taxClassifyCode: 'Tax Code',
      taxRate: 'Tax Rate',
      invoiceNotSet: 'Not configured',
      categoryRent: 'Rent',
      categoryProperty: 'Property Mgmt',
      categoryUtility: 'Utility',
      categoryDeposit: 'Deposit',
      categoryMarketing: 'Marketing',
      categoryIntention: 'Intention',
      categoryOther: 'Other',
      calcTypeSqMonthly: 'Per sqm/month',
      calcTypeFixedMonthly: 'Fixed/month',
      calcTypeFixedOnce: 'Fixed once',
      calcTypePercent: 'Percentage',
      feeAttribute: 'Fee Attribute',
      feeAttributeCost: 'Cost',
      feeAttributeFee: 'Fee',
      feeAttributePlaceholder: 'Select fee attribute',
      collectionMethod: 'Collection Method',
      collectionMethodDeduct: 'Deduct',
      collectionMethodCash: 'Cash',
      collectionMethodPlaceholder: 'Select collection method',
      systemBuilt: 'System',
      required: 'Required',
      optional: 'Optional',
      searchPlaceholder: 'Fee code or name',
      categoryPlaceholder: 'All categories',
      statusPlaceholder: 'All status',
      itemCodePlaceholder: 'Enter fee code (e.g. RENT_BASE)',
      itemNamePlaceholder: 'Enter fee name',
      calcTypePlaceholder: 'Select billing method',
      defaultValuePlaceholder: 'Enter default value',
      unitPlaceholder: 'e.g. CNY, month',
      remarkPlaceholder: 'Remark (optional)',
      invoiceGoodsNamePlaceholder: 'e.g. Real estate leasing service',
      taxClassifyCodePlaceholder: '19-digit tax classification code',
      selectTaxClassifyCode: 'Please select leaf-level tax classification code',
      autoFromTaxCode: 'Auto-filled from tax classification code',
      hintSqMonthly: 'CNY/sqm/month',
      hintFixedMonthly: 'CNY/month',
      hintFixedOnce: 'CNY',
      hintPercent: '%',
      taxRateHint: 'e.g. 0.09 = 9%',
      activeText: 'On',
      inactiveText: 'Off',
      itemCodeRequired: 'Fee code is required',
      itemCodePattern: 'Code must start with uppercase letter, only uppercase letters, numbers and underscores',
      itemNameRequired: 'Fee name is required',
      itemNameLength: 'Name cannot exceed 100 characters',
      categoryRequired: 'Please select a category',
      calcTypeRequired: 'Please select a billing method',
      fetchFailed: 'Failed to fetch fee item list',
      deleteConfirm: 'Are you sure to delete fee item "{name}"?',
    },

    // ======== Business Type ========
    businessType: {
      subtitle: 'Manage mall business type classification with multi-level support',
      addType: 'Add Type',
      addTopLevel: 'Add Top-level Type',
      addChild: 'Add Sub-type',
      addChildBtn: 'Add Child',
      editType: 'Edit Type',
      typeList: 'Business Type List',
      typeName: 'Type Name',
      typeCode: 'Code',
      level: 'Level',
      color: 'Color',
      sortOrder: 'Sort',
      icon: 'Icon',
      description: 'Description',
      topLevel: 'Top',
      childItems: '{count} children',
      topCategory: 'Top Category',
      statusPlaceholder: 'All',
      searchPlaceholder: 'Type name or code',
      typeNamePlaceholder: 'Enter business type name',
      typeCodePlaceholder: 'Enter business type code (e.g. RETAIL)',
      parentPlaceholder: 'Select parent (leave empty for top-level)',
      iconPlaceholder: 'Lucide icon name (e.g. ShoppingBag)',
      descPlaceholder: 'Type description (optional)',
      remarkPlaceholder: 'Remark (optional)',
      autoGenerate: 'Auto Generate',
      activeText: 'On',
      inactiveText: 'Off',
      // Validation
      typeNameRequired: 'Type name is required',
      typeNameLength: 'Name must be 1-100 characters',
      typeCodeRequired: 'Type code is required',
      typeCodePattern: 'Code must start with uppercase letter, only uppercase letters, numbers and underscores allowed',
      typeCodeExists: 'Type code already exists, please change',
      // Messages
      fetchFailed: 'Failed to fetch business type list',
      deleteConfirm: 'Are you sure to delete business type "{name}"? Cannot delete if it has sub-types.',
      createSuccess: 'Created successfully',
      updateSuccess: 'Updated successfully',
    },

    // ======== Payment Method ========
    paymentMethod: {
      title: 'Payment Cycle',
      subtitle: 'Manage contract payment cycles with cycle settings and late fee rules',
      addMethod: 'Add Payment Cycle',
      editMethod: 'Edit Payment Cycle',
      methodList: 'Payment Cycle List',
      methodCode: 'Code',
      methodName: 'Name',
      cycleType: 'Cycle Type',
      cycleDays: 'Cycle',
      unitMonths: 'months',
      unitDays: 'days',
      unitDay: 'day',
      defaultDueDays: 'Due Period',
      defaultDueDaysHint: 'Days after billing to make payment',
      cycleMonthsHint: 'Calculated by month, adapts to month length',
      cycleDaysHint: 'Calculated by fixed days',
      billTiming: 'Bill Timing',
      billDay: 'Bill Day',
      endOfMonth: 'EOM',
      billDayHint: 'Select "EOM" to use the last day of each month automatically',
      selectBillDay: 'Select bill day',
      lateFee: 'Late Fee',
      lateFeeEnabled: 'Enable Late Fee',
      lateFeeType: 'Late Fee Type',
      lateFeeValue: 'Late Fee Value',
      lateFeeCap: 'Late Fee Cap',
      graceDays: 'Grace Days',
      isSystem: 'System',
      days: 'days',
      yuan: 'Yuan',
      // Cycle Types
      cycleMonth: 'Monthly',
      cycleQuarter: 'Quarterly',
      cycleSemiAnnual: 'Semi-Annual',
      cycleYear: 'Yearly',
      cycleCustom: 'Custom',
      // Bill Timing
      billCurrent: 'Current Month',
      billAdvance: 'Advance',
      billNext: 'Next Month',
      // Late Fee Types
      lateFeeFixed: 'Fixed Amount',
      lateFeePercentDaily: 'Daily Percentage',
      lateFeePercentOnce: 'One-time Percentage',
      // Form Sections
      basicInfo: 'Basic Information',
      cycleSettings: 'Payment Cycle Settings',
      dueDateSettings: 'Payment Due Settings',
      billSettings: 'Billing Settings',
      lateFeeSettings: 'Late Fee Settings',
      otherSettings: 'Other Settings',
      // Due Date Types
      dueDateType: 'Due Date Type',
      dueDateTypeDays: 'Days After',
      dueDateTypeSpecific: 'Specific Day',
      dueValue: 'Due Value',
      dueDaysHint: 'Days after bill date to complete payment',
      dueDateHint: 'Day of month for payment due (e.g. 25th). Supports cross-month (e.g. bill on 25th, due on 10th of next month)',
      // Placeholders
      searchPlaceholder: 'Code or name',
      methodCodePlaceholder: 'Enter payment method code (e.g. MONTHLY)',
      methodNamePlaceholder: 'Enter payment method name',
      // Hints
      lateFeeCapHint: 'Maximum late fee amount, 0 means no limit',
      graceDaysHint: 'Grace period days after due date, no late fee during grace period',
      // Validation
      methodCodeRequired: 'Payment method code is required',
      methodCodePattern: 'Code can only contain letters, numbers and underscores',
      methodNameRequired: 'Payment method name is required',
      cycleTypeRequired: 'Cycle type is required',
      cycleDaysRequired: 'Cycle days is required',
      billTimingRequired: 'Bill timing is required',
      // Messages
      fetchFailed: 'Failed to fetch payment method list',
      deleteConfirm: 'Are you sure to delete payment method "{name}"?',
      cannotDeleteSystem: 'System built-in payment method cannot be deleted',
      cannotDisableSystem: 'System built-in payment method cannot be disabled',
    },

    // ======== Billing Entity ========
    signCompany: {
      title: 'Billing Entity Management',
      addCompany: 'Add Billing Entity',
      editCompany: 'Edit Billing Entity',
      companyName: 'Company Name',
      companyCode: 'Credit Code',
      legalPerson: 'Legal Person',
      bankName: 'Bank Name',
      bankAccount: 'Bank Account',
      businessScope: 'Business Scope',
      companyNamePh: 'Enter company name',
      companyCodePh: 'Enter credit code',
      businessScopePh: 'e.g. rent, property fee, utilities',
      // Validation
      companyNameRequired: 'Company name is required',
      companyNameLength: 'Company name must be 2-200 characters',
      // Messages
      fetchFailed: 'Failed to fetch billing entity list',
      createSuccess: 'Billing entity created successfully',
      updateSuccess: 'Billing entity updated successfully',
      deleteConfirm: 'Are you sure to delete billing entity "{name}"?',
    },

    // ======== Print Template ========
    printTemplate: {
      title: 'Print Template',
      subtitle: 'Manage visual print templates with drag-and-drop designer',
      addTemplate: 'Add Template',
      editTemplate: 'Edit Template',
      designTemplate: 'Design Template',
      templateList: 'Template List',
      templateName: 'Template Name',
      templateKey: 'Business Type',
      paperType: 'Paper Size',
      orientation: 'Orientation',
      isDefault: 'Default',
      version: 'Version',
      // Business type options
      keyContract: 'Contract',
      keyBill: 'Bill',
      keyMonthlyFee: 'Monthly Fee',
      keyRenovation: 'Renovation Notice',
      keyCollection: 'Collection Notice',
      // Paper options
      paperA4: 'A4',
      paperA5: 'A5',
      paperCustom: 'Custom',
      // Orientation options
      portrait: 'Portrait',
      landscape: 'Landscape',
      // Placeholders
      searchPlaceholder: 'Template name',
      templateKeyPlaceholder: 'All Types',
      statusPlaceholder: 'All Status',
      templateNamePlaceholder: 'Enter template name',
      remarkPlaceholder: 'Remark (optional)',
      // Actions
      setDefault: 'Set Default',
      cloneTemplate: 'Clone',
      design: 'Design',
      preview: 'Preview',
      // Designer
      designerTitle: 'Template Designer',
      dragHint: 'Drag elements from left panel to canvas',
      basicElements: 'Basic Elements',
      fieldPanel: 'Business Fields',
      propertyPanel: 'Properties',
      saveDesign: 'Save Design',
      clearCanvas: 'Clear Canvas',
      // Validation
      templateNameRequired: 'Template name is required',
      templateKeyRequired: 'Please select business type',
      // Messages
      fetchFailed: 'Failed to fetch template list',
      createSuccess: 'Template created successfully',
      updateSuccess: 'Template updated successfully',
      deleteConfirm: 'Are you sure to delete template "{name}"?',
      setDefaultSuccess: 'Set as default template',
      cloneSuccess: 'Template cloned successfully',
      saveDesignSuccess: 'Template design saved',
      clearConfirm: 'Are you sure to clear canvas? This cannot be undone.',
      noTemplate: 'No custom template, using default print style',
    },

    // ======== Notification Template ========
    notificationTemplate: {
      title: 'Notification Template',
      subtitle: 'Manage notification templates with dynamic configuration',
      addTemplate: 'Add Template',
      editTemplate: 'Edit Template',
      templateList: 'Template List',
      templateKey: 'Template Key',
      templateName: 'Template Name',
      type: 'Type',
      titlePattern: 'Title Pattern',
      contentPattern: 'Content Pattern',
      linkPattern: 'Link Pattern',
      recipientRoles: 'Recipient Roles',
      notifyCreator: 'Notify Creator',
      priority: 'Priority',
      bizType: 'Business Type',
      channels: 'Channels',
      description: 'Description',
      sortOrder: 'Sort Order',
      isSystem: 'System',
      // Business types
      bizTypeContract: 'Contract',
      bizTypeBilling: 'Billing',
      bizTypeRenovation: 'Renovation',
      bizTypeIntention: 'Intention',
      bizTypeWorkOrder: 'Work Order',
      bizTypeTask: 'Task',
      // Placeholders
      templateKeyPlaceholder: 'Enter template key (e.g. CONTRACT_SUBMITTED)',
      templateNamePlaceholder: 'Enter template name',
      templateKeyRequired: 'Template key is required',
      templateKeyMaxLength: 'Template key cannot exceed 100 characters',
      templateNameRequired: 'Template name is required',
      templateNameMaxLength: 'Template name cannot exceed 100 characters',
      typeRequired: 'Please select notification type',
      titlePatternPlaceholder: 'e.g. Contract Pending - {bizNo}',
      titlePatternRequired: 'Title pattern is required',
      titlePatternMaxLength: 'Title pattern cannot exceed 200 characters',
      contentPatternPlaceholder: 'e.g. Contract {bizNo} has been submitted for approval. Supports {bizId} and {bizNo} placeholders',
      contentPatternRequired: 'Content pattern is required',
      contentPatternMaxLength: 'Content pattern cannot exceed 500 characters',
      linkPatternPlaceholder: 'e.g. /contract/rent?id={bizId}',
      recipientRolesPlaceholder: 'Role keys, comma-separated, e.g. admin,finance_manager',
      descriptionPlaceholder: 'Template description',
      priorityRequired: 'Please select priority',
      bizTypeRequired: 'Please select business type',
      // Notification types
      typeBusiness: 'Business',
      typeApproval: 'Approval',
      typeSystem: 'System',
      // Priority
      priorityHigh: 'High',
      priorityNormal: 'Normal',
      priorityLow: 'Low',
      // Channels
      channelInApp: 'In-App',
      channelInAppMini: 'In-App + Mini App',
      channelMiniApp: 'Mini App',
      // Messages
      fetchFailed: 'Failed to fetch notification template list',
      cannotDeleteSystem: 'System built-in templates cannot be deleted',
      deleteConfirm: 'Are you sure to delete notification template "{name}"?',
    },

    // ======== Pay Instrument payInstrument ========
    payInstrument: {
      title: 'Payment Instruments',
      subtitle: 'Manage payment instruments (Cash, WeChat, Alipay, Bank Card, etc.)',
      addItem: 'Add Instrument',
      editItem: 'Edit Instrument',
      instrumentCode: 'Code',
      instrumentName: 'Name',
      instrumentType: 'Type',
      processMode: 'Process Mode',
      isSales: 'Count as Sales',
      isInvoice: 'Can Invoice',
      sortOrder: 'Sort',
      statusText: 'Status',
      remark: 'Remark',
      typeCash: 'Cash',
      typeWechat: 'WeChat Pay',
      typeAlipay: 'Alipay',
      typeCoupon: 'Coupon',
      typeBankCard: 'Bank Card',
      typeCheck: 'Check',
      typeGiftCard: 'Gift Card',
      modeNoLimit: 'No Limit',
      modeNoChange: 'No Change Given',
      modeNoOverpay: 'No Overpayment',
      searchPlaceholder: 'Code or name',
      instrumentCodePlaceholder: 'Enter code (e.g. CASH)',
      instrumentNamePlaceholder: 'Enter instrument name',
      instrumentCodeRequired: 'Please enter instrument code',
      instrumentNameRequired: 'Please enter instrument name',
      fetchFailed: 'Failed to fetch payment instrument list',
      deleteConfirm: 'Are you sure to delete payment instrument "{name}"?',
    },

    // ======== Code Rule codeRule ========
    codeRule: {
      title: 'Code Rules',
      subtitle: 'Manage system code generation rule configurations',
      ruleList: 'Rule List',
      editRule: 'Edit Code Rule',
      ruleName: 'Rule Name',
      codePattern: 'Code Pattern',
      patternHierarchical: 'Hierarchical',
      patternPureSeq: 'Pure Sequence',
      patternDateSeq: 'Date Sequence',
      prefix: 'Prefix',
      prefixPlaceholder: 'e.g. PRD, USR',
      separator: 'Separator',
      separatorPlaceholder: 'e.g. -',
      seqLength: 'Seq Length',
      digits: 'digits',
      example: 'Example',
      genMode: 'Gen Mode',
      autoGen: 'Auto Generate',
      manualGen: 'Manual Input',
      basicInfo: 'Basic Info',
      codeConfig: 'Code Config',
      otherSettings: 'Other Settings',
      dateFormat: 'Date Format',
      resetCycle: 'Reset Cycle',
      resetDaily: 'Daily Reset',
      resetYearly: 'Yearly Reset',
      resetNever: 'Never Reset',
      maxLevel: 'Max Level',
      levelLengths: 'Level Lengths',
      levelLabel: 'Level {n}',
      preview: 'Code Preview',
      ruleNameRequired: 'Please enter rule name',
      seqLengthRequired: 'Please enter sequence length',
      fetchFailed: 'Failed to fetch code rule list',
    },

    // ======== Common (within system module) ========
    common: {
      keyword: 'Keyword',
      statusNormal: 'Normal',
      statusDisabled: 'Disabled',
      statusStopped: 'Stopped',
      enable: 'Enable',
      disable: 'Disable',
      stop: 'Stop',
      enableShort: 'On',
      stopShort: 'Off',
      create: 'Create',
      update: 'Update',
      user: 'User',
    },

    // ======== Scheduled Task Logs jobLog ========
    jobLog: {
      title: 'Scheduled Task Logs',
      subtitle: 'View system scheduled task execution records',
      taskName: 'Task Name',
      taskGroup: 'Task Group',
      cronExpr: 'Cron Expression',
      status: 'Status',
      success: 'Success',
      fail: 'Failed',
      startTime: 'Start Time',
      endTime: 'End Time',
      duration: 'Duration(ms)',
      resultMsg: 'Result',
      errorMsg: 'Error',
      allTasks: 'All Tasks',
      searchPlaceholder: 'Search task name',
      cleanLogs: 'Clean Logs',
      cleanConfirm: 'Are you sure to clean task logs older than 30 days? This cannot be undone.',
      cleanSuccess: 'Cleaned successfully',
      cleanFailed: 'Clean failed',
      trigger: 'Trigger',
      triggerConfirm: 'Are you sure to manually trigger task "{name}"?',
      triggerSuccess: 'Task triggered',
      triggerFailed: 'Trigger failed',
      fetchFailed: 'Failed to fetch task logs',
      logDetail: 'Log Detail',
      noData: 'No data',
    },

    // ======== System Config sysConfig ========
    sysConfig: {
      title: 'System Config',
      subtitle: 'Manage system parameters',
      noData: 'No configuration items',
      groupWatermark: 'Watermark',
      groupSecurity: 'Security',
      groupNotification: 'Notification',
    },
  },
}
